import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AccountStatementFilterComponent } from './account-statement-filter/account-statement-filter';
import { LocationIcon } from '../../../../shared/ui/icons/location-icon/location-icon';
import { HumanIcon } from '../../../../shared/ui/icons/human-icon/human-icon';
import { IdHashIcon } from '../../../../shared/ui/icons/id-hash-icon/id-hash-icon';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';
import { Tables } from '../../../../shared/ui/shared-tables/tables/tables';
import { PharmacySearchItem } from '../../../../pages/admin-dashboard/account-statement/account-statement-auto-complete/account-statement-auto-complete.model';
import {
  StatementFilters,
  InvoiceItem,
  StatementQueryParams,
  InvoiceListItemDTO,
  StatementAccountResponse,
} from '../../../../pages/admin-dashboard/account-statement/account-statement-veiw-list/account-statement-veiw-list.model';
import { ErrorData } from '../../../../utils/error-data/error-data';
import { Loading } from '../../../../utils/loading/loading';
import { PharmacySnackIcon } from '../../../../shared/ui/icons/pharmacy-snack-icon/pharmacy-snack-icon';
import { AccountStatementListDetails } from '../../../../services/Account-Statement/account-statement-list-details';

export type LoadState = 'loading' | 'success' | 'error';

@Component({
  selector: 'app-account-statement-list',
  standalone: true,
  imports: [
    CommonModule,
    AccountStatementFilterComponent,
    Tables,
    Pagination,
    LocationIcon,
    HumanIcon,
    IdHashIcon,
    ErrorData,
    Loading,
    PharmacySnackIcon,
  ],
  templateUrl: './account-statement-list.html',
  styleUrl: './account-statement-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountStatementList implements OnInit {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private accountStatementService = inject(AccountStatementListDetails);

  state = signal<LoadState>('loading');
  pharmacyId = signal<number | null>(null);
  selectedPharmacy = signal<PharmacySearchItem | null>(null);
  appliedFilters = signal<StatementFilters | null>(null);
  isFiltersApplied = computed(() => this.appliedFilters() !== null);

  rows = signal<InvoiceItem[]>([]);
  statistics = signal({ total: 0, unpaid: 0, partiallyPaid: 0 });

  currentPage = signal<number>(1);
  itemsPerPage = signal<number>(25);
  totalItems = signal<number>(0);

  totalPages = computed(() => Math.ceil(this.totalItems() / this.itemsPerPage()) || 1);
  startItem = computed(() => (this.totalItems() === 0 ? 0 : (this.currentPage() - 1) * this.itemsPerPage() + 1));
  endItem = computed(() => Math.min(this.currentPage() * this.itemsPerPage(), this.totalItems()));

  pageNumbers = computed(() => {
    const pages: number[] = [];
    const max = this.totalPages();
    const current = this.currentPage();

    if (max <= 5) {
      for (let i = 1; i <= max; i++) pages.push(i);
    } else {
      if (current <= 3) {
        pages.push(1, 2, 3, 4, -1, max);
      } else if (current >= max - 2) {
        pages.push(1, -1, max - 3, max - 2, max - 1, max);
      } else {
        pages.push(1, -1, current - 1, current, current + 1, -1, max);
      }
    }
    return pages;
  });

  tableHeaderData = signal([
    { key: 'invoiceId', label: 'رقم الفاتورة' },
    { key: 'orderId', label: 'رقم الطلب' },
    { key: 'totalPrice', label: 'إجمالي سعر الصيدلية', type: 'currency' },
    { key: 'statusObj', label: 'الحالة', type: 'status' },
    { key: 'createdOnBy', label: 'تاريخ الإنشاء في/بواسطة', type: 'createdOnBy' },
  ]);

  currentActions = signal([]);

  ngOnInit(): void {
    const historyState = history.state as { pharmacy?: PharmacySearchItem };
    if (historyState?.pharmacy) {
      this.selectedPharmacy.set(historyState.pharmacy);
      this.pharmacyId.set(historyState.pharmacy.id);
      this.loadData();
    } else {
      this.router.navigate(['/Account-Statement']);
    }
  }

  loadData(): void {
    const pharmacyId = this.pharmacyId();
    if (!pharmacyId) return;

    this.state.set('loading');

    const queryParams = this.buildQueryParams();

    this.accountStatementService
      .getStatementAccount(pharmacyId, queryParams)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.handleApiResponse(response),
        error: (err) => {
          console.error('Error fetching statement:', err);
          this.state.set('error');
        },
      });
  }

  private buildQueryParams(): StatementQueryParams {
    const filters = this.appliedFilters();
    const query: StatementQueryParams = {
      pageNum: this.currentPage() - 1,
      pageSize: this.itemsPerPage(),
      orderBy: filters?.orderBy || 'INVOICE_DATE',
      orderDir: filters?.orderDir || 'DESC',
    };

    if (filters) {
      if (filters.invoiceId) query.invoiceId = filters.invoiceId;
      if (filters.orderId) query.orderId = filters.orderId;
      if (filters.dateFrom) query.createdOnFrom = filters.dateFrom;
      if (filters.dateTo) query.createdOnTo = filters.dateTo;
      if (filters.amountFrom !== null) query.totalPriceFrom = Number(filters.amountFrom);
      if (filters.amountTo !== null) query.totalPriceTo = Number(filters.amountTo);
    }

    return query;
  }

  private handleApiResponse(response: StatementAccountResponse): void {
    if (response.summary?.pharmacy) {
      const backendPharmacy = response.summary.pharmacy;
      this.selectedPharmacy.set({
        id: backendPharmacy.id,
        pharmacyId: backendPharmacy.id.toString(),
        ownerName: backendPharmacy.pharmacyOwner?.fullName || '---',
        pharmacyName: backendPharmacy.nickName || '---',
        location: backendPharmacy.address || '---',
      });
    }

    const pendingCount = response.summary?.pendingCount || 0;
    const partiallyPaidCount = response.summary?.partiallyPaidCount || 0;
    this.statistics.set({
      total: pendingCount + partiallyPaidCount,
      unpaid: pendingCount,
      partiallyPaid: partiallyPaidCount,
    });

    this.totalItems.set(response.total || 0);
    this.rows.set(this.mapInvoicesToUI(response.list || []));

    this.state.set('success');
  }

  private mapInvoicesToUI(list: InvoiceListItemDTO[]): InvoiceItem[] {
    return list.map((item) => ({
      id: item.id,
      invoiceId: `#${item.id}`,
      orderId: `#${item.orderId}`,
      totalPrice: item.totalStorePrice ?? 0,
      statusObj: {
        titleEn: this.mapStatusToUIClass(item.invoiceStatus?.titleEn),
        titleAr: item.invoiceStatus?.titleAr || '---',
      },
      createdOn: item.createdOn,
      createdBy: item.createdBy?.fullName || '---',
    }));
  }

  private mapStatusToUIClass(status: string): string {
    if (!status) return 'closed';
    const s = status.toLowerCase();
    if (s.includes('partial')) return 'pending';
    if (s.includes('pending')) return 'closed';
    return 'closed';
  }

  onApplyFilters(filters: StatementFilters): void {
    this.appliedFilters.set(filters);
    this.currentPage.set(1);
    this.loadData();
  }

  onResetFilters(): void {
    this.appliedFilters.set(null);
    this.currentPage.set(1);
    this.loadData();
  }

  onChangePageNumber(size: number): void {
    this.itemsPerPage.set(size);
    this.currentPage.set(1);
  }

  onPageSizeChange(): void {
    this.loadData();
  }

  onGoToPage(page: number): void {
    this.currentPage.set(page);
    this.loadData();
  }

  onPreviousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update((p) => p - 1);
      this.loadData();
    }
  }

  onNextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((p) => p + 1);
      this.loadData();
    }
  }

  toggleDropdown(event: any) {}
  handleAction(event: any) {}
}
